import React, { FunctionComponent, useState, useCallback } from 'react';
import MedicationIcon from '@mui/icons-material/Medication';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from './MedicationEntry.module.css';

interface Medication {
  name: string;
  dosage: string;
  unit: string;
  frequency: string;
  timeOfDay: string[];
}

interface MedicationEntryProps {
  id: string;
  label: string;
  medications: Medication[];
  onChange: (medications: Medication[]) => void;
  allowMultiple?: boolean;
  required?: boolean;
  helperText?: string;
  error?: string;
  hasNextButton?: boolean;
  onNext?: () => void;
}

const MedicationEntry: FunctionComponent<MedicationEntryProps> = ({
  id,
  label,
  medications,
  onChange,
  allowMultiple = true,
  required = false,
  helperText,
  error,
  hasNextButton = false,
  onNext
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const dosageUnits = ['mg', 'mcg', 'g', 'mL', 'units', 'tablets', 'capsules'];
  const frequencies = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'Every 12 hours',
    'As needed',
    'Once weekly',
    'Twice weekly'
  ];
  const timesOfDay = ['Morning', 'Noon', 'Evening', 'Bedtime', 'With meals'];

  // Mock medication suggestions
  const medicationSuggestions = [
    'Metformin', 'Lisinopril', 'Atorvastatin', 'Amlodipine', 'Omeprazole',
    'Levothyroxine', 'Gabapentin', 'Sertraline', 'Hydrochlorothiazide'
  ].filter(med => med.toLowerCase().includes(searchQuery.toLowerCase()));

  const addMedication = useCallback(() => {
    const newIndex = medications.length;
    onChange([
      ...medications,
      {
        name: '',
        dosage: '',
        unit: 'mg',
        frequency: 'Once daily',
        timeOfDay: []
      }
    ]);
    // Collapse all other cards and expand only the new one
    setExpandedCards(new Set([newIndex]));
  }, [medications, onChange]);

  const toggleCardExpansion = (index: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const isCardExpanded = (index: number) => expandedCards.has(index);

  const isCardComplete = (medication: Medication) => {
    return medication.name.trim() !== '' && 
           medication.dosage !== '' && 
           medication.timeOfDay.length > 0;
  };

  // Auto-add first medication if user already indicated they take medications
  React.useEffect(() => {
    if (!isInitialized && medications.length === 0 && hasNextButton) {
      // User already said "Yes" to taking medications, so show the form immediately
      addMedication();
      setIsInitialized(true);
    }
  }, [medications.length, hasNextButton, isInitialized, addMedication]);

  const removeMedication = (index: number) => {
    onChange(medications.filter((_, i) => i !== index));
  };

  const updateMedication = (index: number, field: keyof Medication, value: any) => {
    const updated = [...medications];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleNext = () => {
    if (medications.length === 0 && required) {
      // User said "Yes" but hasn't added any medications
      const shouldContinue = window.confirm(
        "You selected 'Yes' but haven't added any medications. Would you like to continue without listing them?"
      );
      if (shouldContinue && onNext) {
        onNext();
      }
    } else if (onNext) {
      onNext();
    }
  };

  const hasMedications = medications.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
        {allowMultiple && (
          <button
            type="button"
            className={styles.addButton}
            onClick={addMedication}
          >
            <AddIcon />
            Add Medication
          </button>
        )}
      </div>

      <div className={styles.medicationsList}>
        {medications.length === 0 && !hasNextButton && (
          <div className={styles.emptyState}>
            <MedicationIcon className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>Ready to add your medications?</h3>
            <p className={styles.emptyDescription}>
              We'll help you understand your coverage and find cheaper alternatives when available.
            </p>
            <button
              type="button"
              className={styles.addFirstButton}
              onClick={addMedication}
            >
              Add your first medication
            </button>
          </div>
        )}

        {medications.map((medication, index) => {
          const expanded = isCardExpanded(index);
          const complete = isCardComplete(medication);
          
          return (
            <div key={index} className={`${styles.medicationCard} ${expanded ? styles.expanded : styles.collapsed}`}>
              <div 
                className={styles.medicationHeader}
                onClick={() => toggleCardExpansion(index)}
              >
                <MedicationIcon className={styles.medicationIcon} />
                <div className={styles.medicationSummary}>
                  <span className={styles.medicationNumber}>
                    {medication.name || `Medication ${index + 1}`}
                  </span>
                  {!expanded && complete && (
                    <span className={styles.medicationDetails}>
                      {medication.dosage} {medication.unit} • {medication.frequency}
                      {medication.timeOfDay.length > 0 && ` • ${medication.timeOfDay.join(', ')}`}
                    </span>
                  )}
                  {!expanded && !complete && (
                    <span className={styles.incompleteLabel}>Incomplete</span>
                  )}
                </div>
                <div className={styles.headerActions}>
                  {allowMultiple && medications.length > 1 && (
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMedication(index);
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${styles.expandButton} ${expanded ? styles.rotated : ''}`}
                  >
                    <ExpandMoreIcon />
                  </button>
                </div>
              </div>

              {expanded && (
                <div className={styles.medicationFields}>
                  {/* Basic Information Section */}
                  <div className={styles.formSection}>
                    <h5 className={styles.sectionTitle}>Basic Information</h5>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Medication Name</label>
                      <div className={styles.autocompleteWrapper}>
                        <input
                          type="text"
                          value={medication.name}
                          onChange={(e) => {
                            updateMedication(index, 'name', e.target.value);
                            setSearchQuery(e.target.value);
                            setShowAutocomplete(index);
                          }}
                          onFocus={() => setShowAutocomplete(index)}
                          onBlur={() => setTimeout(() => setShowAutocomplete(null), 200)}
                          className={styles.input}
                          placeholder="Start typing medication name..."
                          required={required}
                        />
                        {showAutocomplete === index && searchQuery && medicationSuggestions.length > 0 && (
                          <div className={styles.autocompleteDropdown}>
                            {medicationSuggestions.map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                className={styles.suggestion}
                                onClick={() => {
                                  updateMedication(index, 'name', suggestion);
                                  setSearchQuery('');
                                  setShowAutocomplete(null);
                                }}
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dosage Section */}
                  <div className={styles.formSection}>
                    <h5 className={styles.sectionTitle}>Dosage</h5>
                    <div className={styles.dosageRow}>
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Amount</label>
                        <input
                          type="number"
                          value={medication.dosage}
                          onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                          className={styles.input}
                          placeholder="e.g., 500"
                          required={required}
                        />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.fieldLabel}>Unit</label>
                        <select
                          value={medication.unit}
                          onChange={(e) => updateMedication(index, 'unit', e.target.value)}
                          className={styles.select}
                        >
                          {dosageUnits.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Schedule Section */}
                  <div className={styles.formSection}>
                    <h5 className={styles.sectionTitle}>Schedule</h5>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>How often do you take it?</label>
                      <select
                        value={medication.frequency}
                        onChange={(e) => updateMedication(index, 'frequency', e.target.value)}
                        className={styles.select}
                      >
                        {frequencies.map((freq) => (
                          <option key={freq} value={freq}>{freq}</option>
                        ))}
                      </select>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>When do you take it?</label>
                      <div className={styles.timeOfDayOptions}>
                        {timesOfDay.map((time) => (
                          <label key={time} className={styles.timeOption}>
                            <input
                              type="checkbox"
                              checked={medication.timeOfDay.includes(time)}
                              onChange={(e) => {
                                const newTimes = e.target.checked
                                  ? [...medication.timeOfDay, time]
                                  : medication.timeOfDay.filter(t => t !== time);
                                updateMedication(index, 'timeOfDay', newTimes);
                              }}
                              className={styles.checkbox}
                            />
                            <span className={styles.checkmark}></span>
                            <span className={styles.timeLabel}>{time}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>



      {/* Add Another Button */}
      {hasMedications && allowMultiple && (
        <button
          type="button"
          className={styles.addAnotherButton}
          onClick={addMedication}
        >
          <AddIcon />
          Add another medication
        </button>
      )}

      {/* Next Button */}
      {hasNextButton && onNext && (
        <div className={styles.nextButtonContainer}>
          <button
            type="button"
            className={styles.nextButton}
            onClick={handleNext}
          >
            Continue
          </button>
        </div>
      )}

      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
};

export default MedicationEntry;